import React from 'react';
import type { SimpleBlogItem } from '../utils/BlogListProvider';
import LOGO_PLACEHOLDER from '../assets/images/icons8-blog-150.png';

export interface BlogPostDetailsProps {
    blogItem: SimpleBlogItem;
}

function formatDisplayDate(date: Date | null): string {
    if (!date) return '';
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
}

function BlogPostDetails({ blogItem }: BlogPostDetailsProps) {
    const imageUrl = blogItem.logoUrl?.trim() || LOGO_PLACEHOLDER;
    const dateObj = blogItem.created ?? blogItem.modified;
    const displayDate = formatDisplayDate(dateObj);
    const dateIso = dateObj ? dateObj.toISOString().slice(0, 10) : undefined;

    const keywordsList = blogItem.keywords?.split(/[,;]/).map((s) => s.trim()).filter(Boolean) ?? [];
    const sourcesList : string[] = blogItem.contentSources?.length ? blogItem.contentSources : [];

    const categories = keywordsList.length > 0
        ? keywordsList
        : blogItem.city || blogItem.country
            ? [blogItem.city, blogItem.country].filter(Boolean)
            : ['Uncategorized'];

    return (
        <section id="blog-details" className="blog-details section">
            <div className="container">
                <article className="article">
                    <div className="post-img">
                        <img src={imageUrl} alt={blogItem.title} className="img-fluid" />
                    </div>

                    <h2 className="title">{blogItem.title}</h2>

                    <div className="meta-top">
                        <ul>
                            {blogItem.contactName && (
                                <li className="d-flex align-items-center">
                                    <i className="bi bi-person"></i>{' '}
                                    <span>{blogItem.contactName}</span>
                                </li>
                            )}
                            {displayDate && (
                                <li className="d-flex align-items-center">
                                    <i className="bi bi-clock"></i>{' '}
                                    <time dateTime={dateIso}>{displayDate}</time>
                                </li>
                            )}
                            {blogItem.blogUrl && (
                                <li className="d-flex align-items-center">
                                    <i className="bi bi-link-45deg"></i>{' '}
                                    <a href={blogItem.blogUrl} target="_blank" rel="noopener noreferrer">
                                        Visit blog
                                    </a>
                                </li>
                            )}
                        </ul>
                    </div>

                    {blogItem.description?.trim() && (
                        <div
                            className="content"
                            dangerouslySetInnerHTML={{ __html: blogItem.description }}
                        />
                    )}

                    <div className="meta-bottom">
                        {categories.length > 0 && (
                            <div>
                                <i className="bi bi-folder pe-2"></i>
                                <ul className="cats">
                                    {categories.map((cat, i) => (
                                        <li key={i}><span className="badge bg-primary me-1">{cat}</span></li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {sourcesList.length > 0 && (
                            <div>
                                <i className="bi bi-tags pe-2"></i>
                                <span>Source:</span><br />
                                <ul className="sources">
                                    {sourcesList.map((tag, i) => (
                                        <li key={i}><a href="#">{tag}</a></li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </article>
            </div>
        </section>
    );
}

export default BlogPostDetails;
